import React, { useState, useEffect, useCallback, useMemo } from 'react';
import * as Plot from "@observablehq/plot";
import PlotFigure from '../PlotFigure';
import { apiClient } from '../utils/api';
import ErrorMessage from '../components/common/ErrorMessage';
import { useTheme, THEMES } from '../theme/ThemeContext';
import '../styles/dashboard.css';

const REFRESH_OPTIONS = [
    { label: 'Off', value: 0 },
    { label: '5s', value: 5000 },
    { label: '10s', value: 10000 },
    { label: '30s', value: 30000 },
    { label: '1m', value: 60000 },
    { label: '5m', value: 300000 },
    { label: '10m', value: 600000 },
];

const INTERVAL_OPTIONS = [
    { label: '5 Minutes', value: '5m' },
    { label: '1 Hour', value: '1h' },
    { label: '6 Hours', value: '6h' },
    { label: '12 Hours', value: '12h' },
    { label: '24 Hours', value: '24h' },
];

export const MonitorPage = () => {
    const { theme } = useTheme();
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [refreshInterval, setRefreshInterval] = useState(10000); // Default 10s
    const [timeRange, setTimeRange] = useState('1h');
    const [lastUpdated, setLastUpdated] = useState(null);

    const isLight = theme === THEMES.LIGHT;
    const textColor = isLight ? "#1a1a1a" : "#e2e2e2";
    const gridColor = isLight ? "#e0e0e0" : "#333";

    const loadData = useCallback(async () => {
        // Only set loading true if it's the first load or manual refresh, not auto-refresh
        // But for simplicity we handle it in useEffect
        setError(null);
        try {
            const data = await apiClient.getHeartbeatHistory(timeRange);
            // Expected format: { history: [ { mac, time }, ... ] }
            const parsed = (data.history || []).map(item => ({
                ...item,
                time: new Date(item.time)
            }));
            setHistory(parsed);
            setLastUpdated(new Date());
        } catch (err) {
            console.error(err);
            setError(err.message || 'Failed to load heartbeat history');
        }
    }, [timeRange]);

    // Initial load
    useEffect(() => {
        setLoading(true);
        loadData().finally(() => setLoading(false));
    }, [loadData]);

    // Auto-refresh interval
    useEffect(() => {
        if (refreshInterval <= 0) return;

        const intervalId = setInterval(() => {
            loadData();
        }, refreshInterval);

        return () => clearInterval(intervalId);
    }, [refreshInterval, loadData]);

    const binInterval = useMemo(() => {
        switch (timeRange) {
            case '5m': return '10 seconds'; // High res for short window
            case '1h': return '1 minute';
            case '6h': return '5 minutes';
            case '12h': return '10 minutes';
            case '24h': return '30 minutes';
            default: return '1 minute';
        }
    }, [timeRange]);

    const plotOptions = useMemo(() => {
        if (!history.length) return null;

        return {
            title: "Device Connectivity Flow",
            marginLeft: 120,
            marginBottom: 50,
            style: {
                background: "transparent",
                color: textColor,
                fontSize: "13px",
                fontFamily: "var(--font-mono, monospace)" // specific font for technical look
            },
            x: {
                type: "time",
                grid: true,
                label: "Time",
                tickFormat: "%H:%M",
                line: true
            },
            y: {
                grid: true,
                label: null, // Hide label as MACs are self-explanatory
                domain: [...new Set(history.map(d => d.mac))].sort(),
                line: true,
                tickSize: 0, // Clean look
                padding: 0.2 // Space between device rows
            },
            color: {
                legend: true,
                scheme: isLight ? "Tableau10" : "Spectral",
                label: "Device"
            },
            marks: [
                // Background stripes for rows
                Plot.ruleY([...new Set(history.map(d => d.mac))], {
                    stroke: gridColor,
                    strokeOpacity: 0.1,
                    strokeWidth: 40 // approximate row height to create a "lane" effect
                }),
                // The Activity Strips (Flow)
                Plot.rectX(history, Plot.binX(
                    { fill: "first", title: (d) => `Device: ${d[0].mac}\nCount: ${d.length}\nTime: ${d[0].time.toLocaleTimeString()}` },
                    {
                        x: "time",
                        y: "mac",
                        interval: binInterval,
                        fill: "mac",
                        inset: 0, // No gap between time bins to create solid flow
                    }
                )),
                // Overlay dots for precise event visibility on hover (optional, kept subtle)
                Plot.dot(history, {
                    x: "time",
                    y: "mac",
                    fill: "white",
                    fillOpacity: 0, // Invisible normally
                    r: 3,
                    tip: true,
                    title: d => `Device: ${d.mac}\nTime: ${d.time.toLocaleString()}`
                })
            ]
        };
    }, [history, textColor, gridColor, isLight, binInterval]);

    return (
        <div className="dashboard-container">
            <div className="dashboard-header">
                <h1>Monitor</h1>
                <div className="dashboard-controls" style={{ display: 'flex', gap: '1.5rem', alignItems: 'flex-end' }}>
                    <div className="device-dropdown" style={{ margin: 0, gap: '0.5rem' }}>
                        <label style={{ fontSize: '0.75rem', marginBottom: 0 }}>Time Range</label>
                        <select
                            value={timeRange}
                            onChange={(e) => setTimeRange(e.target.value)}
                            style={{ minWidth: '140px', padding: '0.7rem 1.2rem', fontSize: '0.9rem' }}
                        >
                            {INTERVAL_OPTIONS.map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </select>
                    </div>

                    <div className="device-dropdown" style={{ margin: 0, gap: '0.5rem' }}>
                        <label style={{ fontSize: '0.75rem', marginBottom: 0 }}>Refresh Rate</label>
                        <select
                            value={refreshInterval}
                            onChange={(e) => setRefreshInterval(Number(e.target.value))}
                            style={{ minWidth: '160px', padding: '0.7rem 1.2rem', fontSize: '0.9rem' }}
                        >
                            {REFRESH_OPTIONS.map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </select>
                    </div>

                    <button
                        onClick={() => { setLoading(true); loadData().finally(() => setLoading(false)); }}
                        className="refresh-button"
                        style={{ height: '42px', marginBottom: '2px' }}
                    >
                        Refresh
                    </button>
                </div>
            </div>

            {error && <ErrorMessage message={error} />}

            <div className="monitor-content">
                {lastUpdated && (
                    <div className="last-updated" style={{ color: textColor, opacity: 0.7 }}>
                        Last updated: {lastUpdated.toLocaleTimeString()}
                    </div>
                )}

                {loading && !history.length ? (
                    <div className="loading-message">Loading data...</div>
                ) : (
                    <div className="plot-container" style={{ padding: '20px', background: 'var(--bg-secondary)', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
                        {plotOptions ? (
                            <PlotFigure options={plotOptions} />
                        ) : (
                            <div className="no-data-message" style={{ color: textColor }}>No heartbeat data found for this period.</div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default MonitorPage;
